import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { isIndia } from "@/lib/edition";
import { MapPin, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Cascading Country → State → City finder for the institution directory.
// Calls onChange({ country, state, city }) whenever a selection changes.
// If onGo is provided, a "Go" button appears to jump to the country page.
export default function InstitutionLocationFinder({ value, onChange, onGo }) {
  const [tree, setTree] = useState([]);
  useEffect(() => { api.get("/institutions/locations").then((r) => setTree(r.data || [])).catch(() => setTree([])); }, []);

  const country = isIndia ? "India" : (value.country || "all");
  const state = value.state || "all";
  const city = value.city || "all";

  const countryNode = useMemo(() => tree.find((c) => c.country === country), [tree, country]);
  const states = countryNode ? countryNode.states.filter((s) => s.state !== "—") : [];
  const stateNode = states.find((s) => s.state === state);
  const cities = stateNode ? stateNode.cities : [];

  const setCountry = (v) => onChange({ country: v === "all" ? "" : v, state: "", city: "" });
  const setState = (v) => onChange({ ...value, state: v === "all" ? "" : v, city: "" });
  const setCity = (v) => onChange({ ...value, city: v === "all" ? "" : v });

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="location-finder">
      <span className={`mr-1 inline-flex items-center gap-1.5 text-sm font-medium ${isIndia ? "text-primary" : "text-secondary"}`}><MapPin size={15} weight="duotone" /> Find your School/College</span>
      {!isIndia && (
      <Select value={country} onValueChange={setCountry}>
        <SelectTrigger className="w-40 rounded-full" data-testid="finder-country"><SelectValue placeholder="Country" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="all">All countries</SelectItem>
          {tree.map((c) => <SelectItem key={c.country} value={c.country}>{c.country}</SelectItem>)}
        </SelectContent>
      </Select>
      )}
      <Select value={state} onValueChange={setState} disabled={!countryNode || states.length === 0}>
        <SelectTrigger className="w-40 rounded-full" data-testid="finder-state"><SelectValue placeholder="State / region" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="all">All states</SelectItem>
          {states.map((s) => <SelectItem key={s.state} value={s.state}>{s.state}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={city} onValueChange={setCity} disabled={!stateNode || cities.length === 0}>
        <SelectTrigger className="w-40 rounded-full" data-testid="finder-city"><SelectValue placeholder="City" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="all">All cities</SelectItem>
          {cities.map((ci) => <SelectItem key={ci} value={ci}>{ci}</SelectItem>)}
        </SelectContent>
      </Select>
      {onGo && (
        <Button type="button" onClick={() => onGo(value)} disabled={!countryNode}
          className={`rounded-full gap-1.5 ${isIndia ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-secondary text-secondary-foreground hover:bg-secondary/90"}`} data-testid="finder-go">
          Go <ArrowRight size={15} weight="bold" />
        </Button>
      )}
    </div>
  );
}
